/**
 * NoteCards — the content review's note-card layout of a thread (opted into
 * with `layout="cards"` on ChatThread). One card per top-level note: a header
 * (avatar, name, relative time), the moment as pills, the text and files, the
 * replies nested inside in their own inner cards, and a footer with Reply and
 * the hover-revealed Edit/Delete. Every rule — who may do what, what a moment
 * seeks or opens — is ChatThread's, handed in through `NoteThreadContext`.
 */
import { useMemo } from 'react';
import { isMediaAnchor, type EntryAttachment, type MediaMessageAnchor } from '@discovr/types';

import { ArrowBendUpLeft, PencilSimple, Trash } from '../../../design-system/icons';
import { formatLocalDateTime, formatShortRelativeTime } from '../../../lib/datetime';
import { useNow } from '../../../campaign-detail/hooks/useNow';
import { cn } from '../../../lib/utils';
import { DaySeparator, JumpFlash, RejectionEntry, type FlashTarget } from './ChatEntryParts';
import {
  DELETE_MESSAGE_LABEL,
  DELETE_TITLE,
  EDIT_MESSAGE_LABEL,
  EDIT_TITLE,
  dayKey,
  hasAnyAction,
  isLongBody,
  isMine,
  isOptimisticEntry,
  isRejectionEntry,
  senderInitials,
  senderLabelFor,
  showsBodyText,
  type ChatThreadEntry,
  type MessagePermissions,
} from './chatEntries';
import type { FileSource } from './fileSource';
import { ATTACHMENTS_CARD, MessageAttachments } from './MessageAttachments';
import { MOMENT_BLOCK_CARD, MomentBlock } from './MomentBlock';
import { momentFlags } from './moments';
import { groupNoteCards, type NoteCardGroup } from './noteCardGroups';

/** Relative times ("42m") step once a minute. */
const RELATIVE_TIME_TICK_MS = 60_000;
const NO_ATTACHMENTS: EntryAttachment[] = [];
const REPLY_LABEL = 'Reply';
const EDITED_LABEL = 'edited';
const TIME_SEPARATOR = '·';
const READ_MORE_LABEL = 'Read more';
const SHOW_LESS_LABEL = 'Show less';
const REPLY_ICON_PX = 16;
const MANAGE_ICON_PX = 13;

export const NOTE_CARD_TESTID = 'note-card';
export const NOTE_REPLY_TESTID = 'note-reply';

type NoteSide = 'mine' | 'theirs';

const LIST_CLASS = 'flex flex-col gap-3';
/** Mine sits right with a quarter-width indent on the left; theirs mirrors it. */
const CARD_CLASS: Record<NoteSide, string> = {
  mine: 'group/note relative flex flex-col gap-2.5 rounded-[12px] border border-(--brand-500) bg-(--brand-50) p-3.5 ml-[25%]',
  theirs: 'group/note relative flex flex-col gap-2.5 rounded-[12px] border border-border bg-card p-3.5 mr-[25%]',
};
const FOOTER_CLASS: Record<NoteSide, string> = {
  mine: 'flex items-center gap-1 border-t border-(--brand-200) pt-2',
  theirs: 'flex items-center gap-1 border-t border-border pt-2',
};
const REPLY_CARD_CLASS = 'relative flex flex-col gap-2 rounded-[10px] border border-border bg-card p-3';
const REPLIES_CLASS = 'flex flex-col gap-2';
const HEADER_CLASS = 'flex min-w-0 items-center gap-2';
const NAME_CLASS = 'min-w-0 flex-1 truncate text-[14px]/5 font-semibold text-foreground';
const TIME_CLASS = 'flex shrink-0 items-center gap-1 text-[12px]/4 text-muted-foreground';
const EDITED_CLASS = 'italic';
const AVATAR_CLASS = 'flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full';
const AVATAR_INITIALS_CLASS = 'border border-border bg-muted text-[10px] font-semibold text-foreground/80';
const AVATAR_IMAGE_CLASS = 'size-full object-cover';
const BODY_CLASS = 'whitespace-pre-wrap wrap-anywhere text-[14px]/[22px] text-foreground';
const BODY_CLAMP_CLASS = 'line-clamp-4';
const READ_MORE_CLASS = 'mt-1 text-[13px] font-medium text-(--brand-600) hover:underline';
const REPLY_BUTTON_CLASS =
  '-ml-2 inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-[14px] font-medium text-foreground/80 transition-colors hover:bg-black/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const MANAGE_BUTTON_CLASS =
  'flex size-6 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const DELETE_BUTTON_CLASS =
  'flex size-6 items-center justify-center rounded-full text-muted-foreground hover:bg-rose-50 hover:text-rose-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
/** Edit/Delete stay out of the way until their own card is hovered or one of them has focus. */
const NOTE_MANAGE_CLASS = 'ml-auto flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover/note:opacity-100';

/** What the cards need from ChatThread: its rules, its handlers and its state. */
export interface NoteThreadContext {
  outgoingActor: string;
  flashTarget: FlashTarget | null;
  expanded: ReadonlySet<string>;
  onToggleExpanded: (id: string) => void;
  permissionsFor: (entry: ChatThreadEntry) => MessagePermissions;
  /** Stages a reply to `entry`; `label` names its sender on the composer's reply chip. */
  onReply: (entry: ChatThreadEntry, label: string) => void;
  onEdit: (entry: ChatThreadEntry) => void;
  onDelete: (id: string) => void;
  currentContentVersionId?: string;
  onSeekToMoment?: (tMs: number) => void;
  fileSource?: FileSource;
  openFrameFor: (entry: ChatThreadEntry, anchor: MediaMessageAnchor) => () => void;
  attachmentOpenerFor: (attachments: EntryAttachment[], optimistic: boolean) => ((index: number) => void) | undefined;
}

type NoteCardView = NoteThreadContext & { nowMs: number };

/** A top-level card in the list, with the day separator it opens when its day changes. */
interface NoteListItem {
  group: NoteCardGroup;
  startsDay: boolean;
}

function startsNewDay(previous: NoteCardGroup | undefined, group: NoteCardGroup): boolean {
  if (!previous) return true;
  return dayKey(previous.entry.created_at) !== dayKey(group.entry.created_at);
}

/** Day separators belong to top-level cards only — a reply stays inside its parent's card whatever its day. */
function withDayStarts(groups: NoteCardGroup[]): NoteListItem[] {
  return groups.map((group, index) => ({ group, startsDay: startsNewDay(groups[index - 1], group) }));
}

function sideOf(entry: ChatThreadEntry, outgoingActor: string): NoteSide {
  if (isMine(entry.actor, outgoingActor)) return 'mine';
  return 'theirs';
}

function NoteAvatar({ entry, label }: { entry: ChatThreadEntry; label: string }) {
  if (entry.sender_avatar) {
    return (
      <span className={AVATAR_CLASS} aria-hidden>
        <img src={entry.sender_avatar} alt="" className={AVATAR_IMAGE_CLASS} />
      </span>
    );
  }
  return (
    <span className={cn(AVATAR_CLASS, AVATAR_INITIALS_CLASS)} aria-hidden>
      {senderInitials(entry, label)}
    </span>
  );
}

function NoteTime({ entry, nowMs }: { entry: ChatThreadEntry; nowMs: number }) {
  const relative = formatShortRelativeTime(entry.created_at, nowMs);
  const showsSeparator = !!entry.edited && !!relative;
  return (
    <span className={TIME_CLASS}>
      {entry.edited ? <span className={EDITED_CLASS}>{EDITED_LABEL}</span> : null}
      {showsSeparator ? <span aria-hidden>{TIME_SEPARATOR}</span> : null}
      {relative ? (
        <time dateTime={entry.created_at} title={formatLocalDateTime(entry.created_at)}>
          {relative}
        </time>
      ) : null}
    </span>
  );
}

interface NoteCardHeaderProps {
  entry: ChatThreadEntry;
  label: string;
  nowMs: number;
}

function NoteCardHeader({ entry, label, nowMs }: NoteCardHeaderProps) {
  return (
    <div className={HEADER_CLASS}>
      <NoteAvatar entry={entry} label={label} />
      <span className={NAME_CLASS}>{label}</span>
      <NoteTime entry={entry} nowMs={nowMs} />
    </div>
  );
}

interface NoteBodyProps {
  entry: ChatThreadEntry;
  attachments: EntryAttachment[];
  expanded: boolean;
  onToggleExpanded: (id: string) => void;
}

function NoteBody({ entry, attachments, expanded, onToggleExpanded }: NoteBodyProps) {
  if (!showsBodyText(entry.body, attachments)) return null;
  const long = isLongBody(entry.body);
  const clamped = long && !expanded;

  function handleToggle(): void {
    onToggleExpanded(entry.id);
  }

  return (
    <div>
      <p className={cn(BODY_CLASS, clamped && BODY_CLAMP_CLASS)}>{entry.body}</p>
      {long ? (
        <button type="button" onClick={handleToggle} className={READ_MORE_CLASS}>
          {expanded ? SHOW_LESS_LABEL : READ_MORE_LABEL}
        </button>
      ) : null}
    </div>
  );
}

/** The moment pills, the text and the files — the same in a card and in a reply. */
function NoteContent({ entry, view }: { entry: ChatThreadEntry; view: NoteCardView }) {
  const mediaAnchor = isMediaAnchor(entry.anchor) ? entry.anchor : undefined;
  const attachments = entry.attachments ?? NO_ATTACHMENTS;
  const optimistic = isOptimisticEntry(String(entry.id));
  return (
    <>
      {mediaAnchor ? (
        <MomentBlock
          variant={MOMENT_BLOCK_CARD}
          anchor={mediaAnchor}
          bodyEmpty={!entry.body.trim()}
          {...momentFlags(mediaAnchor, view.currentContentVersionId, view.onSeekToMoment)}
          onSeek={view.onSeekToMoment}
          onOpenFrame={view.openFrameFor(entry, mediaAnchor)}
          localFrameUrl={entry.local_frame_url}
          fileSource={view.fileSource}
        />
      ) : null}
      <NoteBody entry={entry} attachments={attachments} expanded={view.expanded.has(entry.id)} onToggleExpanded={view.onToggleExpanded} />
      {attachments.length > 0 ? (
        <MessageAttachments
          variant={ATTACHMENTS_CARD}
          attachments={attachments}
          fileSource={view.fileSource}
          onOpen={view.attachmentOpenerFor(attachments, optimistic)}
        />
      ) : null}
    </>
  );
}

interface ManageButtonsProps {
  entry: ChatThreadEntry;
  permissions: MessagePermissions;
  view: NoteCardView;
  className: string;
}

/** Edit and Delete, each only where the server granted it; nothing at all when neither is. */
function ManageButtons({ entry, permissions, view, className }: ManageButtonsProps) {
  if (!permissions.canEdit && !permissions.canDelete) return null;

  function handleEdit(): void {
    view.onEdit(entry);
  }

  function handleDelete(): void {
    view.onDelete(entry.id);
  }

  return (
    <span className={className}>
      {permissions.canEdit ? (
        <button type="button" onClick={handleEdit} aria-label={EDIT_MESSAGE_LABEL} title={EDIT_TITLE} className={MANAGE_BUTTON_CLASS}>
          <PencilSimple size={MANAGE_ICON_PX} weight="bold" />
        </button>
      ) : null}
      {permissions.canDelete ? (
        <button type="button" onClick={handleDelete} aria-label={DELETE_MESSAGE_LABEL} title={DELETE_TITLE} className={DELETE_BUTTON_CLASS}>
          <Trash size={MANAGE_ICON_PX} weight="bold" />
        </button>
      ) : null}
    </span>
  );
}

interface NoteCardActionsProps {
  entry: ChatThreadEntry;
  label: string;
  side: NoteSide;
  permissions: MessagePermissions;
  view: NoteCardView;
}

/** The footer: Reply on the left, Edit/Delete on the right. None on an optimistic card, which has no actions. */
function NoteCardActions({ entry, label, side, permissions, view }: NoteCardActionsProps) {
  if (!hasAnyAction(permissions)) return null;

  function handleReply(): void {
    view.onReply(entry, label);
  }

  return (
    <div className={FOOTER_CLASS[side]}>
      {permissions.canReply ? (
        <button type="button" onClick={handleReply} className={REPLY_BUTTON_CLASS}>
          <ArrowBendUpLeft size={REPLY_ICON_PX} aria-hidden />
          {REPLY_LABEL}
        </button>
      ) : null}
      <ManageButtons entry={entry} permissions={permissions} view={view} className={NOTE_MANAGE_CLASS} />
    </div>
  );
}

/** A reply inside its parent's card: its own header, moment, text and files. The design gives a reply no actions of its own. */
function NoteReplyCard({ entry, view }: { entry: ChatThreadEntry; view: NoteCardView }) {
  const label = senderLabelFor(entry, view.outgoingActor);
  return (
    <div data-message-id={entry.id} tabIndex={-1} className={REPLY_CARD_CLASS} data-testid={NOTE_REPLY_TESTID}>
      <JumpFlash target={view.flashTarget} messageId={entry.id} />
      <NoteCardHeader entry={entry} label={label} nowMs={view.nowMs} />
      <NoteContent entry={entry} view={view} />
    </div>
  );
}

function NoteCard({ group, view }: { group: NoteCardGroup; view: NoteCardView }) {
  const { entry, replies } = group;
  const side = sideOf(entry, view.outgoingActor);
  const label = senderLabelFor(entry, view.outgoingActor);
  return (
    <article data-message-id={entry.id} tabIndex={-1} className={CARD_CLASS[side]} data-testid={NOTE_CARD_TESTID} data-side={side}>
      <JumpFlash target={view.flashTarget} messageId={entry.id} />
      <NoteCardHeader entry={entry} label={label} nowMs={view.nowMs} />
      <NoteContent entry={entry} view={view} />
      {replies.length > 0 ? (
        <div className={REPLIES_CLASS}>
          {replies.map((reply) => (
            <NoteReplyCard key={reply.id} entry={reply} view={view} />
          ))}
        </div>
      ) : null}
      <NoteCardActions entry={entry} label={label} side={side} permissions={view.permissionsFor(entry)} view={view} />
    </article>
  );
}

function NoteListEntry({ group, view }: { group: NoteCardGroup; view: NoteCardView }) {
  if (isRejectionEntry(group.entry)) return <RejectionEntry entry={group.entry} outgoingActor={view.outgoingActor} />;
  return <NoteCard group={group} view={view} />;
}

export interface NoteCardListProps {
  /** The thread, already sorted chronologically (optimistic entries included). */
  entries: ChatThreadEntry[];
  thread: NoteThreadContext;
}

export function NoteCardList({ entries, thread }: NoteCardListProps) {
  const nowMs = useNow(RELATIVE_TIME_TICK_MS);
  const items = useMemo(() => withDayStarts(groupNoteCards(entries)), [entries]);
  const view: NoteCardView = { ...thread, nowMs };
  return (
    <div className={LIST_CLASS}>
      {items.map(({ group, startsDay }) => (
        <NoteListItemView key={group.entry.id} group={group} startsDay={startsDay} view={view} />
      ))}
    </div>
  );
}

function NoteListItemView({ group, startsDay, view }: NoteListItem & { view: NoteCardView }) {
  return (
    <>
      {startsDay ? <DaySeparator iso={group.entry.created_at} /> : null}
      <NoteListEntry group={group} view={view} />
    </>
  );
}
